import { queryOptions } from "@tanstack/react-query";
import { getNewsReactions, toggleNewsReaction } from "@/lib/api/generated/endpoints/news/news";
import {
  isNewsReactionEmoji,
  normalizeNewsReactionTargets,
  type NewsReactionEmoji,
  type NewsReactionItem,
  type NewsReactionTarget,
  type NewsReactionTargetType,
} from "@/lib/utils/news-reaction";
import type { NewsReactionItem as GeneratedNewsReactionItem } from "@/lib/api/generated/model";

export function newsReactionQueryKey(targets: NewsReactionTarget[]) {
  const normalized = normalizeNewsReactionTargets(targets);
  return [
    "news-reactions",
    ...normalized.map((target) => `${target.targetType}:${target.targetId}`),
  ];
}

export function newsReactionsQueryOptions(targets: NewsReactionTarget[]) {
  const normalized = normalizeNewsReactionTargets(targets);
  if (normalized.length === 0) {
    return queryOptions({
      queryKey: [...newsReactionQueryKey(normalized), "empty"],
      queryFn: async () => [] as NewsReactionItem[],
      staleTime: 30_000,
    });
  }

  return queryOptions({
    queryKey: newsReactionQueryKey(normalized),
    queryFn: () => getNewsReactions({ targets: normalized }).then(normalizeReactionItems),
    staleTime: 30_000,
  });
}

export async function toggleNewsReactionAction(params: {
  targetType: NewsReactionTargetType;
  targetId: string;
  emoji: NewsReactionEmoji;
}) {
  return toggleNewsReaction(params);
}

function normalizeReactionItems(items: GeneratedNewsReactionItem[]): NewsReactionItem[] {
  return items.flatMap((item) => {
    if (item.targetType !== "news" && item.targetType !== "comment") {
      return [];
    }
    if (!isNewsReactionEmoji(item.emoji)) {
      return [];
    }

    return [
      {
        targetType: item.targetType,
        targetId: item.targetId,
        emoji: item.emoji,
        count: item.count,
        isReactedByCurrentUser: item.isReactedByCurrentUser,
      },
    ];
  });
}
